import { useState, useEffect, useCallback, useRef } from 'react';
import { Badge, Btn, Card, SectionHeader, DataTable, SearchBar, Pagination, Avatar } from '../../components/ui';
import { Icon } from '../../icons';
import { getAuditLogs, exportAuditLogs } from '../../api/audit';
import { usePermissions } from '../../hooks/usePermissions';
import { getErrorMessage, normalizePagination } from '../../utils/apiUtils';
import { formatDateByPreference } from '../../utils/formatters';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function AuditLogScreen() {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.admin?.[key] || translations.en?.admin?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [logs, setLogs] = useState([]);

  // Filter & Pagination State
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [module, setModule] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [meta, setMeta] = useState(normalizePagination(null));
  const dateInputRef = useRef(null);

  const openDatePicker = () => {
    if (dateInputRef.current) {
      if (typeof dateInputRef.current.showPicker === 'function') {
        dateInputRef.current.showPicker();
      } else {
        dateInputRef.current.focus();
      }
    }
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (search) params.search = search;
      if (module) params.module = module;
      if (dateFrom) params.date_from = dateFrom;

      const res = await getAuditLogs(params);
      setLogs(res.data || []);
      setMeta(normalizePagination(res.meta));
    } catch (err) {
      console.error(getErrorMessage(err, t("toast_load_logs_failed")));
    } finally {
      setLoading(false);
    }
  }, [page, search, module, dateFrom]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (module) params.module = module;
      if (dateFrom) params.date_from = dateFrom;
      await exportAuditLogs(params);
    } catch (err) {
      alert(getErrorMessage(err, t("toast_export_failed")));
    } finally {
      setExporting(false);
    }
  };

  const getRoleColor = (role) => {
    const r = role?.toLowerCase();
    if (r === 'super_admin') return 'blue';
    if (r === 'admin') return 'amber';
    if (r === 'accountant') return 'purple';
    if (r === 'support') return 'cyan';
    if (r === 'system' || r === 'auto') return 'gray';
    return 'slate';
  };


  const formatRole = (role) => {
    if (!role) return '-';
    const key = `role_${role.toLowerCase()}`;
    const translated = t(key);
    if (translated !== key) return translated;
    
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };


  const translateAction = (text) => {
    if (!text) return text;
    
    let match = text.match(/^(Created|Updated|Deleted) ([a-zA-Z0-9_]+) #(\d+)$/i);
    if (match) {
        const actionKey = `action_${match[1].toLowerCase()}`;
        const modelKey = `model_${match[2].toLowerCase()}`;
        
        const actionTranslated = t(actionKey) !== actionKey ? t(actionKey) : match[1];
        const modelTranslated = t(modelKey) !== modelKey ? t(modelKey) : match[2];
        
        return t("log_model_action", {
             action: actionTranslated, 
             model: modelTranslated, 
             id: match[3] 
        });
    }
    
    match = text.match(/^Ran payroll for (\d+) staff for (.+)$/i);
    if (match) {
        return t("log_ran_payroll", { count: match[1], month: match[2] });
    }

    const translated = t(text);
    return translated !== text ? translated : text;
  };

  const rows = logs.map(log => [
    formatDateByPreference(log.created_at),
    log.user_name === 'System' ? (
      <span className="text-xs font-mono text-ink dark:text-slate-100">{t("text_system")}</span>
    ) : (
      <div className="flex items-center gap-1.5">
        <Avatar name={log.user_name} size={22} />
        <span className="text-xs text-ink dark:text-slate-100">{log.user_name}</span>
      </div>
    ),
    <Badge color={getRoleColor(log.user_role)}>{formatRole(log.user_role)}</Badge>,
    <span className="text-sm text-ink dark:text-slate-200">{translateAction(log.description)}</span>,
    <span className="text-sm capitalize text-ink dark:text-slate-200">{t(log.module)}</span>,
    <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">{log.ip_address || '-'}</span>
  ]);

  return (
    <div className="fade-up space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
        <SearchBar
          value={search}
          onChange={(val) => { setSearch(val); setPage(1); }}
          placeholder={t("placeholder_search_logs")}
          className="w-full sm:w-64"
        />

        <select
          value={module}
          onChange={(e) => { setModule(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-border dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="">{t("opt_all_modules")}</option>
          <option value="auth">{t("opt_auth_security")}</option>
          <option value="users">{t("opt_user_management")}</option>
          <option value="finance">{t("opt_finance")}</option>
          <option value="maintenance">{t("opt_maintenance")}</option>
          <option value="billing">{t("opt_billing")}</option>
        </select>

        <div
          onClick={openDatePicker}
          className="relative flex items-center justify-between gap-2 px-3 py-2 border border-border dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 cursor-pointer hover:border-slate-400 dark:hover:border-slate-600 focus-within:ring-2 focus-within:ring-primary/20 transition-all min-w-[160px]"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="calendar" size={15} className="text-slate-400 dark:text-slate-500 shrink-0" />
            <span className={`truncate ${dateFrom ? 'font-medium text-ink dark:text-slate-100' : 'text-slate-400 dark:text-slate-400'}`}>
              {dateFrom ? formatDateByPreference(dateFrom) : (t("label_select_date") || "Select Date")}
            </span>
          </div>

          <div className="flex items-center gap-1 shrink-0 ml-1">
            {dateFrom && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setDateFrom('');
                  setPage(1);
                }}
                className="p-0.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                title="Clear date"
              >
                <Icon name="x" size={12} />
              </button>
            )}
            <Icon name="chevron-down" size={13} className="text-slate-400 dark:text-slate-500" />
          </div>

          <input
            ref={dateInputRef}
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="sr-only"
            tabIndex={-1}
            aria-label={t("label_select_date") || "Select Date"}
          />
        </div>

        {hasPermission('audit.export') && (
          <Btn sm icon="download" onClick={handleExport} disabled={exporting || logs.length === 0} className="ml-auto">
            {exporting ? t("btn_exporting") : t("btn_export_csv")}
          </Btn>
        )}
      </div>

      <Card>
        <SectionHeader title={t("title_audit_log")} subtitle={t("subtitle_audit_log")} />
        <DataTable
          cols={[t("col_timestamp"), t("col_user"), t("col_role"), t("col_action"), t("col_module"), t("col_ip")]}
          rows={rows}
          loading={loading}
          emptyMsg={search || module ? t("msg_no_logs_filter") : t("msg_no_logs")}
          footer={
            <Pagination
              className=""
              page={meta.current}
              total={meta.total}
              perPage={meta.perPage}
              onPage={(p) => setPage(p)}
            />
          }
        />
      </Card>
    </div>
  );
}
